import React, { useEffect, useState } from "react";
import { Clock, CalendarDays, FileText, AlertCircle } from "lucide-react";
import StatCard from "./StatCard";

import { useUser } from "../../../authentication/authentication";
import textTranslator from "../../../utils/useTextTranslator";
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest, apiSuccess } from "../../../redux/api/apiActions";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../api";
import { ChevronRight } from "lucide-react";
import useWindowSize from "../../../utils/useWindowSize";
/**
 * Text Component Wrapper for Waiting Room Page
 * Props:
 * - children: Text content
 * - className: Additional CSS classes
 * @returns Text JSX
 */

const Text = ({ children, className, page = "PATIENTS" }) => {
  return (
    <Txt page={page} className={className}>
      {children}
    </Txt>
  );
};

function TabSection({ stats }) {
  const [tab, setTab] = useState("Last Visit");
  const tabs = ["Last Visit", "Next Appointment", "Open Bills", "Risk Level"];
  const isMobile = useWindowSize().width < 640;

  const renderContent = () => {
    if (isMobile) {
      switch (tab) {
        case "Last Visit":
          return <StatCard {...stats.find((s) => s.id === "lastVisit")} />;
        case "Next Appointment":
          return (
            <StatCard {...stats.find((s) => s.id === "nextAppointment")} />
          );
        case "Open Bills":
          return <StatCard {...stats.find((s) => s.id === "openBills")} />;
        case "Risk Level":
          return <StatCard {...stats.find((s) => s.id === "riskLevel")} />;
        default:
          return null;
      }
    } else {
      return (
        <>
          {stats.map((s, i) => (
            <StatCard key={i} {...s} />
          ))}
        </>
      );
    }
  };
  return (
    <div>
      {/* tab bar */}

      <div className="flex sm:hidden items-center justify-between my-2">
        <div className="flex overflow-x-auto whitespace-nowrap overflow-y-hidden  bg-white rounded-xl shadow-sm border border-gray-100 w-full">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                tab === t
                  ? "text-blue-500 border-blue-500"
                  : "text-gray-500 border-transparent"
              }`}
            >
              <Text page="VISITS">{t}</Text>
            </button>
          ))}
        </div>
      </div>

      {/* 3-column content */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-4">
        {renderContent()}
      </div>
    </div>
  );
}

function StatsGrid() {
  const { id } = useParams();
  const user = useUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requests } = useSelector((state) => state.api);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const statsKey = `getPatientStats_${id}`;
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState(null);
  useEffect(() => {
    dispatch(apiRequest("GET", statsKey));
    setLoading(true);

    api
      .get(`/visits/patient/stats/${id}`)
      .then((res) => {
        setStatsData(res.data); // store raw API data
        setLoading(false);

        dispatch(
          apiSuccess("GET", statsKey, "Fetched visit stats successfully"),
        );
      })
      .catch(() => {
        setLoading(false);
      });
  }, [id, dispatch, statsKey]);
  useEffect(() => {
    if (!statsData) return;

    const data = statsData;

    const formattedStats = [
      {
        id: "lastVisit",
        label: textTranslator("Last Visit", "VISITS"),
        value: data.lastVisit ? formatDate(data.lastVisit.startedAt) : textTranslator("No visits yet", "VISITS"),
        sub: data.lastVisit?.startedAt
          ? formatTime(data.lastVisit.startedAt.split("T")[1].slice(0, 5))
          : null,
        icon: Clock,
        iconBg: "bg-blue-50",
        iconColor: "text-blue-500",
        cardBg: "bg-blue-100/30",
        border: "border-blue-200",
        status: data.lastVisit?.status || null,
        translatedStatus:
          textTranslator(data.lastVisit?.status, "VISITS") || null,
      },

      {
        id: "nextAppointment",
        label: textTranslator("Next Appointment", "VISITS"),
        value: data.nextAppointment
          ? formatDate(data.nextAppointment.appointmentDate)
          : textTranslator("No upcoming appointments", "VISITS"),
        sub: data.nextAppointment
          ? formatTime(data.nextAppointment.appointmentTime)
          : null,
        icon: CalendarDays,
        iconBg: "bg-green-50",
        iconColor: "text-green-500",
        cardBg: "bg-green-100/30",
        border: "border-green-200",
        status: data.nextAppointment?.status || null,
        translatedStatus:
          textTranslator(data.nextAppointment?.status, "APPOINTMENTS") || null,
      },

      {
        id: "openBills",
        label: textTranslator("Open Bills", "VISITS"),
        value:
          data.openBills?.length > 0
            ? `${data.openBills.length} ${textTranslator("bill(s)", "VISITS")}`
            : textTranslator("No bills", "VISITS"),
        sub:
          data.openBills?.length > 0
            ? `${textTranslator("Total", "PAYMENTS")}: ${data.openBills
                .reduce((sum, b) => sum + parseFloat(b.amount), 0)
                .toFixed(2)} ${textTranslator("MAD", "PAYMENTS")}`
            : null,
        icon: FileText,
        iconBg: "bg-orange-50",
        iconColor: "text-orange-500",
        cardBg: "bg-orange-100/30",
        border: "border-orange-200",
        badge: data.openBills?.length > 0 ? "pending" : null,
        translatedBadge:
          data.openBills?.length > 0
            ? textTranslator("pending", "PAYMENTS")
            : null,
      },

      {
        id: "riskLevel",
        label: textTranslator("Risk Level", "VISITS"),
        value: `${data.riskData?.value}/10` || "Unknown",
        description: textTranslator(
          data.riskData?.description || "No risk data available",
          "VISITS",
        ),
        icon: AlertCircle,
        iconBg:
          data.riskData?.level === "high"
            ? "bg-red-50"
            : data.riskData?.level === "medium"
              ? "bg-yellow-50"
              : "bg-green-50",
        iconColor:
          data.riskData?.level === "high"
            ? "text-red-500"
            : data.riskData?.level === "medium"
              ? "text-yellow-500"
              : "text-green-500",
        badge: data.riskData?.level || null,
        translatedBadge:
          textTranslator(data.riskData?.level, "VISITS") || "unknown",
        cardBg:
          data.riskData?.level === "high"
            ? "bg-red-100/30"
            : data.riskData?.level === "medium"
              ? "bg-yellow-100/30"
              : "bg-green-100/30",
        border:
          data.riskData?.level === "high"
            ? "border-red-200"
            : data.riskData?.level === "medium"
              ? "border-yellow-200"
              : "border-green-200",
      },
    ];

    setStats(formattedStats);
  }, [defaultLanguage, statsData]);

  // Show loading state or empty state
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 mt-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
   
      <TabSection stats={stats} />
   
  );
}

export default StatsGrid;
